import type { CostLibraryTask } from "@shared/schema";
import { deriveCostRecipe, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { markDimensionPriceReview, priceRecipeFromDimensions as priceRecipeForQuote } from "@workspace/api-zod/dimension-pricing";
import { directDerivation, deriveDimensionQuantity, type QuantityDerivation, type QuoteDimension } from "@workspace/api-zod/quantity-dimensions";
import { apiRequest } from "./queryClient";
import { createCatalogueQuoteItem } from "./quote-catalogue-item";
import { quoteRecipeSplit } from "@/components/quote-item-recipe-breakdown";
import { rescaleBreakdown } from "./cost-breakdown";
import type { TemplateMeasurementValues, TemplateOpening } from "./template-measurements";
import { normaliseLibraryName } from "./template-library-bindings";

export type TemplatePriceBand = "low" | "standard" | "high";
export interface TemplateCalculatedItem {
  id: string; section: string; category: string; trade: string;
  name: string; description: string; quantity: number; unit: string; unitCost: number;
  inclusions: string[]; exclusions: string[]; labourHours: number;
  labourCost: number; materialCost: number; plantCost: number;
  catalogueTaskKey?: string; costLibraryTaskId?: string; quoteCostRecipe?: CostRecipe | null;
  libraryNames?: string[];
  quantityDerivation?: QuantityDerivation;
  sourceVariant?: { name?: string; specification?: string };
  pricingWarning?: string;
  measurementWarning?: string;
  isOptional?: boolean;
  priceConfirmed?: boolean;
  priceSource?: "ai_estimated" | "cost_library" | "manual";
  pricingIssue?: "missing_library" | "library_error" | "ai_error";
  /** Template-only scope exclusion, not a unit-price adjustment. */
  quantityAreaExclusion?: { area: number; basis: string };
}
export interface TemplateMeasurementContext {
  projectType: string;
  market?: string;
  width?: number;
  depth?: number;
  values: TemplateMeasurementValues;
  openings: TemplateOpening[];
}
const penny = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
export const canonicalTemplateUnit = (u: string): QuoteDimension["unit"] | null => ({
  sqm: "m²", m2: "m²", "m²": "m²", lm: "m", linear_m: "m", m: "m", metre: "m", metres: "m",
  m3: "m³", "m³": "m³", cum: "m³", cu_m: "m³", each: "each", item: "each", allow: "each", no: "each", nr: "each", unit: "each", kg: "kg",
} as Record<string, QuoteDimension["unit"]>)[u.toLowerCase().trim()] ?? null;
export const compatibleTemplateUnits = (a: string, b: string) => canonicalTemplateUnit(a)
  ? canonicalTemplateUnit(a) === canonicalTemplateUnit(b) : a.trim().toLowerCase() === b.trim().toLowerCase();
export function enteredDimension(id: string, label: string, value: number, unit: QuoteDimension["unit"], note = "Entered by the builder for this quote."): QuoteDimension {
  if (!Number.isFinite(value) || value < 0) throw new Error(`Enter a valid ${label.toLowerCase()}.`);
  return { id, label, value, unit, source: "entered", entryOrigin: "manual", note };
}
const measuredQuantity = (d: QuoteDimension, rationale: string): QuantityDerivation => ({
  version: 1, dimensions: [d], quantity_op: "quantity", quantity_operands: [d.id],
  rationale, note: "", quantity_overridden: false,
});
export function validateTemplateMeasurements(values: TemplateMeasurementValues, openings: TemplateOpening[]) {
  for (const [key, raw] of Object.entries(values)) {
    if (!raw.trim() || key === "steelSection") continue;
    if (!Number.isFinite(Number(raw)) || Number(raw) < 0) throw new Error(`Check the measurement for ${key}.`);
    if (/Count$/.test(key) && !Number.isInteger(Number(raw))) throw new Error("Point and fixture counts must be whole numbers.");
  }
  for (const o of openings) {
    if (![o.width, o.height, o.quantity].every(s => s.trim() && Number.isFinite(Number(s)) && Number(s) > 0)
      || !Number.isInteger(Number(o.quantity))) throw new Error("Enter a positive width, height and whole-number quantity for each opening.");
  }
}
/** Template scope is not measurement evidence. Only explicit inputs drive these quantities. */
export function applyTemplateMeasurements(seeds: TemplateCalculatedItem[], ctx: TemplateMeasurementContext): TemplateCalculatedItem[] {
  validateTemplateMeasurements(ctx.values, ctx.openings);
  const number = (key: string): number | undefined => ctx.values[key]?.trim() ? Number(ctx.values[key]) : undefined;
  const explicitFloor = number("floorArea");
  const footprint = ctx.width && ctx.depth ? ctx.width * ctx.depth : undefined;
  const floorArea = explicitFloor ?? footprint;
  const wallLength = number("wallLength"), wallHeight = number("wallHeight");
  const openingArea = ctx.openings.filter(o => o.kind !== "rooflight" && o.deductFromWalls)
    .reduce((n, o) => n + Number(o.width) / 1000 * Number(o.height) / 1000 * Number(o.quantity), 0);
  const calculatedWall = wallLength != null && wallHeight != null ? wallLength * wallHeight - openingArea : undefined;
  if (calculatedWall != null && calculatedWall < 0) throw new Error("The opening area exceeds the measured wall area.");
  const wallArea = number("wallArea") ?? calculatedWall;
  const ceilingArea = number("ceilingArea") ?? floorArea;
  const finishArea = wallArea != null && ceilingArea != null ? wallArea + ceilingArea : undefined;
  const withMeasurements = seeds.map(seed => {
    const item = { ...seed, quantityDerivation: directDerivation(seed.description || seed.name, "Template quantity allowance — review or enter the actual measurements."),
      measurementWarning: "Template quantity allowance: confirm the quantity or enter measurements." };
    const name = item.name.toLowerCase(), unit = canonicalTemplateUnit(item.unit);
    let amount: number | undefined, label = "";
    if (unit === "m²") {
      if (/roof|felt|batten|rafter/.test(name)) { amount = number("roofArea"); label = "Roof area"; }
      else if (/pav|sub-base/.test(name)) { amount = number("pavingArea"); label = "Paving area"; }
      else if (/lawn|turf/.test(name)) { amount = number("lawnArea"); label = "Lawn area"; }
      else if (/decking/.test(name)) { amount = number("deckingArea"); label = "Decking area"; }
      else if (/tiling|splashback/.test(name) && !/floor/.test(name)) { amount = number("tilingArea"); label = "Wall tiling area"; }
      else if (/^bathroom floor tiling$/.test(name) && ctx.projectType !== "bathroom_refurb") {
        // A bathroom addition/renovation is not the whole property's floor.
        amount = undefined;
      }
      else if (/floor|slab|concrete base/.test(name)) { amount = floorArea; label = "Floor area"; }
      else if (/ceiling/.test(name) && !/wall/.test(name)) { amount = ceilingArea; label = "Ceiling area"; }
      else if (/plaster|skim|decorat|lining/.test(name)) { amount = finishArea; label = "Wall and ceiling area"; }
      else if (/wall|cladding/.test(name)) { amount = wallArea; label = "Net wall area"; }
    } else if (unit === "m") {
      const key = /footing|foundation/.test(name) ? "foundationLength"
        : /skirting/.test(name) ? "skirtingLength"
        : /architrave/.test(name) ? "architraveLength"
        : /window board/.test(name) ? "windowBoardLength"
        : /fenc/.test(name) ? "fenceLength"
        : /fascia|soffit|gutter/.test(name) ? "rooflineLength"
        : /ridge/.test(name) ? "ridgeLength"
        : /worktop|kitchen unit/.test(name) ? "kitchenRun"
        : /drain/.test(name) ? "drainageLength"
        : /pipe/.test(name) ? "pipeLength" : undefined;
      if (key) { amount = number(key); label = key.replace(/([A-Z])/g, " $1"); }
    } else if (unit === "m³") {
      if (/footing|foundation|trench/.test(name)) {
        const length = number("foundationLength"), width = number("foundationWidth"), depth = number("foundationDepth");
        if (length != null && width != null && depth != null) amount = length * width / 1000 * depth / 1000;
        label = "Foundation concrete volume";
      } else if (/slab|concrete/.test(name) && floorArea != null && number("slabThickness") != null) {
        amount = floorArea * number("slabThickness")! / 1000; label = "Slab concrete volume";
      }
    } else if (unit === "each") {
      const key = /socket/.test(name) ? "socketCount" : /lighting point|light point/.test(name) ? "lightingCount" : /radiator/.test(name) ? "radiatorCount" : undefined;
      if (key) { amount = number(key); label = key.replace(/([A-Z])/g, " $1"); }
    }
    let grossArea: number | undefined;
    let assumedGrossArea = false;
    if (unit === "m²" && item.quantityAreaExclusion) {
      assumedGrossArea = amount == null;
      amount ??= seed.quantity;
      label ||= "Gross finishing area";
      grossArea = amount;
      if (amount < item.quantityAreaExclusion.area) throw new Error("Separately tiled bathroom areas exceed the measured finishing area. Review the room quantities.");
      amount -= item.quantityAreaExclusion.area;
    }
    if (amount != null && unit) {
      const d = enteredDimension("template-quantity", label, penny(grossArea ?? amount), unit,
        "Calculated from the builder's template measurements. Edit here to change this quote line only.");
      if (item.quantityAreaExclusion) {
        if (assumedGrossArea) {
          d.source = "assumed";
          delete d.entryOrigin;
        }
      }
      item.quantityDerivation = measuredQuantity(d, item.description || item.name);
      if (item.quantityAreaExclusion) {
        item.quantityDerivation.dimensions.push({
          id: "excluded-finish-area", label: "Excluded bathroom tiled area", unit: "m²",
          value: item.quantityAreaExclusion.area, source: "assumed", note: item.quantityAreaExclusion.basis,
        });
        item.quantityDerivation.quantity_op = "area_difference";
        item.quantityDerivation.quantity_operands.push("excluded-finish-area");
      }
      item.quantity = deriveDimensionQuantity(item.quantityDerivation, item.unit);
      item.measurementWarning = item.quantityAreaExclusion
        ? `Excludes ${item.quantityAreaExclusion.area}m². ${item.quantityAreaExclusion.basis}` : "";
    }
    // Keep the actual builder inputs, not just a flattened calculated area.
    const localBathroomFloor = /^bathroom floor tiling$/.test(name) && ctx.projectType !== "bathroom_refurb";
    const usesFootprint = unit === "m²" && !localBathroomFloor && (
      (/floor|slab|concrete base/.test(name) && explicitFloor == null)
      || (/ceiling/.test(name) && !/wall/.test(name) && number("ceilingArea") == null && explicitFloor == null));
    if (usesFootprint && footprint != null && ctx.width != null && ctx.depth != null) {
      item.quantityDerivation = { ...item.quantityDerivation,
        dimensions: [enteredDimension("footprint-width", "Floor width", ctx.width, "m"),
          enteredDimension("footprint-depth", "Floor length", ctx.depth, "m")],
        quantity_op: "area", quantity_operands: ["footprint-width", "footprint-depth"] };
      if (item.quantityAreaExclusion) {
        item.quantityDerivation.dimensions.push({
          id: "excluded-finish-area", label: "Excluded bathroom tiled floor", unit: "m²",
          value: item.quantityAreaExclusion.area, source: "assumed", note: item.quantityAreaExclusion.basis,
        });
        item.quantityDerivation.quantity_op = "net_area";
        item.quantityDerivation.quantity_operands.push("excluded-finish-area");
      }
      item.quantity = deriveDimensionQuantity(item.quantityDerivation, item.unit);
    }
    if (unit === "m³" && /footing|foundation|trench/.test(name)
      && number("foundationLength") != null && number("foundationWidth") != null && number("foundationDepth") != null) {
      item.quantityDerivation = { ...item.quantityDerivation, quantity_op: "volume",
        dimensions: [enteredDimension("foundation-length", "Foundation length", number("foundationLength")!, "m"),
          enteredDimension("foundation-width", "Foundation width", number("foundationWidth")! / 1000, "m"),
          enteredDimension("foundation-depth", "Foundation depth", number("foundationDepth")! / 1000, "m")],
        quantity_operands: ["foundation-length", "foundation-width", "foundation-depth"] };
    }
    if (unit === "m²" && /wall|cladding/.test(name) && !/plaster|skim|decorat|lining/.test(name)
      && number("wallArea") == null && wallLength != null && wallHeight != null) {
      const ds = [enteredDimension("wall-length", "Wall length", wallLength, "m"),
        enteredDimension("wall-height", "Wall height", wallHeight, "m")];
      if (openingArea) ds.push(enteredDimension("opening-deduction", "Opening deduction", openingArea, "m²"));
      item.quantityDerivation = { ...item.quantityDerivation, dimensions: ds, quantity_op: "wall_area", quantity_operands: ds.map(d => d.id) };
    }
    const supplyInputs = /foundation|footing|trench/.test(name)
      ? [["foundationWidth", "Foundation width"], ["foundationDepth", "Foundation depth"]]
      : /slab|concrete base/.test(name) ? [["slabThickness", "Slab thickness"]]
      : /sub-base/.test(name) ? [["subbaseDepth", "Sub-base depth"]] : [];
    for (const [key, label] of supplyInputs) if (number(key) != null && !item.quantityDerivation.dimensions.some(d => d.label === label))
      item.quantityDerivation.dimensions.push(enteredDimension(`supply-${key}`, label, number(key)! / 1000, "m"));
    // Supply dimensions are per element, not the total run or item count.
    if (/\b(steel|rsj|beam|uc|ub)\b/.test(name) && !/remov|demol|prop|temporary/.test(name)) {
      if (number("steelLength") != null) item.quantityDerivation.dimensions.push(enteredDimension("steel-length", "Steel length", number("steelLength")!, "m"));
      if (ctx.values.steelSection?.trim()) item.description = `${item.description} Section: ${ctx.values.steelSection.trim()}.`;
    }
    // Bundled insulation still uses manually supplied areas as pricing context;
    // these are not a formula for changing its lump-sum work quantity.
    if (unit === "each" && /insulation/.test(name)) {
      for (const [id, label, value] of [
        ["floor-area", "Floor area", floorArea], ["wall-area", "Net wall area", wallArea],
        ["ceiling-area", "Ceiling area", ceilingArea],
      ] as const) if (value != null) item.quantityDerivation.dimensions.push(enteredDimension(id, label, value, "m²"));
    }
    if (!item.quantityDerivation.dimensions.length && unit) {
      const allowance: QuoteDimension = {
        id: "template-allowance", label: "Template quantity", value: item.quantity, unit,
        source: "assumed", note: "Template quantity allowance; no item-specific measurements have been entered.",
      };
      item.quantityDerivation = { ...item.quantityDerivation, dimensions: [allowance],
        quantity_op: unit === "each" ? "count" : "quantity", quantity_operands: [allowance.id] };
    }
    return item;
  });
  // Split actual supply variants, never split removal or other labour-only activities by opening size.
  return withMeasurements.flatMap(item => {
    const name = item.name.toLowerCase();
    if (/remov|demol|strip|making good|make good/.test(name)) return [item];
    const composite = /windows? [&/] doors?|windows? & door/.test(name);
    const kind = /rooflight|roof light|velux/.test(name) ? "rooflight" : /window/.test(name) && !/board/.test(name) ? "window"
      : /door/.test(name) && !/architrave/.test(name) ? "door" : null;
    if (!kind || canonicalTemplateUnit(item.unit) !== "each") return [item];
    const openings = ctx.openings.filter(o => composite ? o.kind !== "rooflight" : o.kind === kind);
    if (!openings.length) return [item];
    return openings.map(o => {
      const d = enteredDimension("opening-count", "Opening count", Number(o.quantity), "each");
      const baseName = composite ? o.kind === "door" ? "Door" : /upvc/i.test(item.description) ? "uPVC Window" : /aluminium/i.test(item.description) ? "Aluminium Window" : "Window" : item.name;
      const openingText = `${item.description} ${o.specification}`.toLowerCase();
      const libraryNames = o.kind === "rooflight" ? ["Install rooflight / Velux window"]
        : o.kind === "window" ? openingText.includes("aluminium")
          ? ["Supply and fit aluminium window", "Aluminium Window"] : ["Supply and fit uPVC window", "uPVC Window"]
        : /fire|fd30/.test(openingText) ? ["Fire door supply and fit (FD30)"]
        : /bi.?fold/.test(openingText) ? ["Install bi-fold doors"]
        : /sliding/.test(openingText) ? ["Install patio sliding doors"]
        : /french/.test(openingText) ? ["Install French doors"]
        : composite ? ["Supply and fit uPVC external door", "uPVC External Door"] : ["Hang internal door"];
      return { ...item, libraryNames, id: `${item.id}-opening-${o.id}`, name: `${baseName} ${Number(o.width)} × ${Number(o.height)}`,
        description: [composite ? `Supply and install ${baseName.toLowerCase()}.` : item.description, o.specification.trim()].filter(Boolean).join(" "),
        quantity: Number(o.quantity), measurementWarning: "",
        quantityDerivation: { ...measuredQuantity(d, item.description || item.name),
          dimensions: [d, enteredDimension("opening-width", `${o.kind} width`, Number(o.width) / 1000, "m"),
            enteredDimension("opening-height", `${o.kind} height`, Number(o.height) / 1000, "m")] },
      };
    });
  });
}

/** Reuses the existing server matcher (including personalised rates), never a parallel client matcher. */
export async function priceTemplateItems<T extends TemplateCalculatedItem>(items: T[], band: TemplatePriceBand, catalogue?: CostLibraryTask[], market = "uk"): Promise<T[]> {
  const results: T[] = [];
  // Bounded concurrency avoids a recipe/matcher request burst on larger templates.
  for (let offset = 0; offset < items.length; offset += 5) {
    results.push(...await Promise.all(items.slice(offset, offset + 5).map(async item => {
      const names = market === "uk" ? item.libraryNames ?? [item.name] : [item.name];
      const usable = (task: CostLibraryTask | null): task is CostLibraryTask => !!task
        && !task.isHidden && task.isActive !== false
        && compatibleTemplateUnits(item.unit, task.unit)
        && (market !== "uk" || names.some(name => normaliseLibraryName(name) === normaliseLibraryName(task.name)));
      let task: CostLibraryTask | null = null;
      for (const name of names) {
        if (catalogue && market === "uk") {
          task = catalogue.find(candidate => normaliseLibraryName(candidate.name) === normaliseLibraryName(name) && usable(candidate)) ?? null;
        } else {
          const params = new URLSearchParams({ category: item.category, name, unit: item.unit });
          const response = await apiRequest("GET", `/api/cost-library/match?${params}`);
          const candidate = (await response.json() as { task: CostLibraryTask | null }).task;
          if (usable(candidate)) task = candidate;
        }
        if (task) break;
      }
      if (!task || task.isHidden || task.isActive === false || !compatibleTemplateUnits(item.unit, task.unit)) {
        return { ...item, unitCost: 0, labourCost: 0, materialCost: 0, plantCost: 0, quoteCostRecipe: undefined,
          costLibraryTaskId: undefined, catalogueTaskKey: undefined, priceConfirmed: false,
          priceSource: undefined, pricingIssue: "missing_library",
          pricingWarning: "No library rate is available for this scope. Review the unit cost or replace this line from Add Items." };
      }
      let priced;
      try {
        priced = await createCatalogueQuoteItem(task, { band, quantity: item.quantity, dimensions: item.quantityDerivation, sectionText: `${item.name} ${item.description}` });
      } catch (error) {
        // Invalid recipes must be visible, not replaced with the retired template rates.
        return { ...item, unitCost: 0, labourCost: 0, materialCost: 0, plantCost: 0, quoteCostRecipe: undefined,
          costLibraryTaskId: undefined, catalogueTaskKey: undefined, priceConfirmed: false,
          priceSource: undefined, pricingIssue: "library_error",
          pricingWarning: error instanceof Error ? error.message : "Could not load the library breakdown." };
      }
      return { ...item, ...priced, id: item.id, section: item.section, name: item.name,
        priceSource: "cost_library", pricingIssue: undefined,
        quantityDerivation: priced.quantityDerivation ?? item.quantityDerivation, measurementWarning: item.measurementWarning,
        // Use the actual priced scope, not a legacy bundle description which may
        // promise work that this library rate does not include.
        description: item.description || task.description || "",
        pricingWarning: task.hasRecipe ? undefined : "Library allowance: no detailed material recipe is configured." };
    })));
  }
  return results;
}
export function repriceTemplateDimensions<T extends TemplateCalculatedItem>(item: T, data: QuantityDerivation, quantity: number): T {
  if (!item.quoteCostRecipe) {
    const unitCost = item.unitCost;
    const split = rescaleBreakdown({ labour: item.labourCost, material: item.materialCost, plant: item.plantCost }, quantity * unitCost, item.category);
    return { ...item, quantity, unitCost, quantityDerivation: markDimensionPriceReview(item.quantityDerivation, data, item.unit), measurementWarning: undefined,
      labourCost: split.labour, materialCost: split.material, plantCost: split.plant };
  }
  const recipe = priceRecipeForQuote(item.quoteCostRecipe, item.unitCost, data, `${item.name} ${item.description} ${item.sourceVariant?.name ?? ""} ${item.sourceVariant?.specification ?? ""}`, quantity, item.unit);
  const unitCost = Number(deriveCostRecipe(recipe).rateTypical);
  return { ...item, quantity, quantityDerivation: data, quoteCostRecipe: recipe, unitCost,
    ...quoteRecipeSplit(recipe, quantity), measurementWarning: undefined };
}
export function confirmTemplateQuantity(item: TemplateCalculatedItem, quantity: number): QuantityDerivation {
  const data = item.quantityDerivation ?? directDerivation(item.description || item.name);
  const unit = canonicalTemplateUnit(item.unit);
  if (!unit) return { ...data, quantity_overridden: true };
  const old = data.quantity_op === "quantity" ? data.dimensions.find(d => d.id === data.quantity_operands[0]) : undefined;
  const next = { ...enteredDimension(old?.id ?? "confirmed-quantity", old?.label ?? "Quantity", quantity, unit),
    ...(old ? { originalValue: old.originalValue ?? old.value, originalSource: old.originalSource ?? old.source } : {}) };
  const retained = data.dimensions.filter(d => !data.quantity_operands.includes(d.id));
  return { ...data, quantity_op: "quantity", quantity_operands: [next.id], dimensions: [next, ...retained], quantity_overridden: false,
    note: "Quantity entered explicitly by the builder." };
}
export function calculateTemplateQuantity(item: TemplateCalculatedItem, op: "length" | "area" | "wall_area" | "volume" | "perimeter",
  inputs: number[], inputUnit: "m" | "mm" = "m") {
  const count = op === "volume" ? 3 : op === "length" ? 1 : op === "wall_area" ? 3 : 2;
  if (inputs.length !== count || inputs.some(n => !Number.isFinite(n) || n < 0))
    throw new Error("Enter every measurement as a non-negative number.");
  const names = op === "wall_area" ? ["Length", "Height", "Opening deduction"] : ["Length", "Width", "Depth"];
  const dimensions = inputs.map((n, i) => enteredDimension(`quantity-${i}`, names[i],
    op === "wall_area" && i === 2 ? n : n / (inputUnit === "mm" ? 1000 : 1),
    op === "wall_area" && i === 2 ? "m²" : "m"));
  const previous = item.quantityDerivation ?? directDerivation(item.description || item.name);
  const operands = dimensions.map(d => d.id);
  const data: QuantityDerivation = { ...previous, dimensions: [...dimensions,
    ...previous.dimensions.filter(d => !previous.quantity_operands.includes(d.id) && !operands.includes(d.id))],
    quantity_op: op, quantity_operands: operands, quantity_overridden: false, note: "Calculated from measurements entered by the builder." };
  const quantity = deriveDimensionQuantity(data, item.unit);
  return repriceTemplateDimensions(item, data, quantity);
}
